import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useInView } from "motion/react";
import NumberFlow from "@number-flow/react";
import confetti from "canvas-confetti";
import TopBar from "../../components/layout/TopBar";
import FooterBar from "../../components/layout/FooterBar";
import RotatingText from "../../components/RotatingText/RotatingText";
import { CyberneticBentoGrid } from "../../components/ui/CyberneticBentoGrid";
import { FeatureSteps } from "../../components/ui/FeatureSteps";
import { SplineScene } from "../../components/ui/SplineScene";
import { Spotlight } from "../../components/ui/Spotlight";
import axiosConfig from "../../config/axiosConfig";
import PricingCalculator from "../dashboard/components/pages/PricingCalculator";
import "./LandingPageNew.css";

// Landing page images
import heroMainImg from "../../assets/images/landing/hero-main.png";
import stepSignup from "../../assets/images/landing/step-signup.jpg";
import stepSetup from "../../assets/images/landing/step-setup.jpg";
import stepLaunch from "../../assets/images/landing/step-launch.jpg";
import dashboardPreview from "../../assets/images/landing/dashboard-preview.jpg";

// ==========================================
// HERO SECTION
// ==========================================
const HeroSection = () => {
  return (
    <section className="hero-section-new landing-section" id="hero">
      <div className="hero-glow-orb orb-1"></div>
      <div className="hero-glow-orb orb-2"></div>

      <div className="landing-container">
        <div className="hero-content-wrapper">
          <div className="hero-text">
            <div className="hero-badge">
              <span>🚀</span> Now Powered by Official WhatsApp API
            </div>
            <h1 className="hero-title">
              Scale Your Business with{" "}
              <RotatingText
                texts={["WhatsApp", "Customers", "Revenue", "Growth"]}
                staggerFrom="last"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-120%" }}
                staggerDuration={0.025}
                splitLevelClassName="rotating-text-split"
                transition={{ type: "spring", damping: 30, stiffness: 400 }}
                rotationInterval={2000}
              />
            </h1>
            <p className="hero-subtitle">
              Stop chasing leads. Start closing them — on WhatsApp.
              Automate campaigns, manage conversations, and turn every chat
              into revenue. Built on the official WhatsApp Business API.
            </p>
            <div className="hero-buttons">
              <Link to="/signup" className="btn-primary">
                Get Early Access
              </Link>
              <Link to="/guide" className="btn-secondary">
                Product Guide Tour
              </Link>
              <a href="#features" className="hero-demo-link">
                ▶ Watch Demo
              </a>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-image-wrapper">
              <img
                src={heroMainImg}
                alt="MessageOS Dashboard"
                loading="lazy"
              />
              <div className="hero-float-card card-1">
                <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" style={{width: '28px', height: '28px'}} />
                <span>WhatsApp API</span>
              </div>
              <div className="hero-float-card card-2">
                <img src="https://pngimg.com/uploads/meta/meta_PNG2.png" alt="Meta" style={{width: '28px', height: '28px'}} />
                <span>Meta Verified</span>
              </div>
              <div className="hero-float-card card-3">
                <img src="https://upload.wikimedia.org/wikipedia/commons/0/05/Facebook_Logo_%282019%29.png" alt="Facebook" style={{width: '28px', height: '28px'}} />
                <span>FB Integration</span>
              </div>
              <div className="hero-float-card card-4">
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg" alt="Gmail" style={{width: '28px', height: '28px'}} />
                <span>Gmail Sync</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==========================================
// PLATFORM PERFORMANCE STATS STRIP
// ==========================================
const PlatformPerformance = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px 0px" });

  const stats = [
    {
      value: "99.9%",
      label: "Uptime Guarantee",
      color: "#22c55e",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
    },
    {
      value: "<1s",
      label: "Message Delivery",
      color: "#3b82f6",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
      ),
    },
    {
      value: "24/7",
      label: "Support Available",
      color: "#8b5cf6",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      ),
    },
    {
      value: "100%",
      label: "Meta Compliant",
      color: "#f59e0b",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      ),
    },
  ];

  return (
    <section className="platform-perf-strip" ref={ref}>
      <div className="landing-container">
        <div className="platform-perf-grid">
          {stats.map((s, i) => (
            <motion.div
              className="platform-perf-stat"
              key={i}
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1, ease: "easeOut" }}
            >
              <span className="platform-perf-icon" style={{ color: s.color }}>
                {s.icon}
              </span>
              <span className="platform-perf-value">{s.value}</span>
              <span
                className="platform-perf-accent"
                style={{ background: s.color }}
              />
              <span className="platform-perf-desc">{s.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==========================================
// WHY US SECTION (with Spline 3D)
// ==========================================
const WhyUsSection = () => {
  const reasons = [
    {
      icon: "🤖",
      title: "AI-Powered Support",
      desc: "Coming soon — intelligent AI that auto-replies to customers, handles FAQs, and learns from your conversations.",
      upcoming: true,
    },
    {
      icon: "⚡",
      title: "Official WhatsApp API",
      desc: "Direct Meta integration — no third-party middleware, no message delays.",
    },
    {
      icon: "🔧",
      title: "Zero-Code Setup",
      desc: "Connect your number and launch campaigns in under 30 minutes.",
    },
    {
      icon: "📈",
      title: "Built to Scale",
      desc: "From 100 to 1M messages — infrastructure that grows with your business.",
    },
  ];

  return (
    <section className="why-us-section" id="why-us">
      <div className="why-us-card">
        <Spotlight className="why-us-spotlight" fill="white" />

        <div className="why-us-inner">
          {/* Left: content */}
          <div className="why-us-content">
            <h2 className="why-us-title">Why Choose MessageOS?</h2>
            <p className="why-us-subtitle">
              We're building the fastest, safest way to engage customers on WhatsApp.
            </p>
            <div className="why-us-reasons">
              {reasons.map((r, idx) => (
                <div className="why-us-reason" key={idx}>
                  <span className="why-us-reason-icon">{r.icon}</span>
                  <div>
                    <h4>
                      {r.title}
                      {r.upcoming && <span className="upcoming-badge">Coming Soon</span>}
                    </h4>
                    <p>{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Spline 3D robot */}
          <div className="why-us-3d">
            <SplineScene
              scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode"
              className="why-us-spline"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// ==========================================
// FEATURES SECTION
// ==========================================
const FeaturesSection = () => {
  return (
    <section className="features-section-new" id="features">
      <div className="landing-container">
        <CyberneticBentoGrid />
      </div>
    </section>
  );
};

// ==========================================
// HOW IT WORKS SECTION (FeatureSteps)
// ==========================================
const HowItWorksSection = () => {
  const steps = [
    {
      step: "Step 1",
      title: "Sign Up & Connect",
      content:
        "Create your account and connect your WhatsApp Business number in minutes. No coding required.",
      image: stepSignup,
    },
    {
      step: "Step 2",
      title: "Set Up Campaigns",
      content:
        "Create message templates, import contacts, and configure automation flows with our intuitive builder.",
      image: stepSetup,
    },
    {
      step: "Step 3",
      title: "Launch & Grow",
      content:
        "Start sending messages, track performance in real-time, and scale your outreach effortlessly.",
      image: stepLaunch,
    },
  ];

  return (
    <section className="how-it-works-section" id="how-it-works">
      <FeatureSteps
        features={steps}
        title="Get Started in 3 Simple Steps"
        subtitle="From signup to sending your first campaign in under 30 minutes"
      />
    </section>
  );
};

// ==========================================
// PRODUCT DEMO SECTION
// ==========================================
const ProductDemoSection = () => {
  return (
    <section className="product-demo-section landing-section" id="demo">
      <div className="landing-container">
        <div className="product-demo-wrapper">
          <div className="product-demo-content">
            <h2>See MessageOS in Action</h2>
            <ul className="product-demo-benefits">
              <li>Intuitive drag-and-drop campaign builder</li>
              <li>Real-time message delivery tracking</li>
              <li>AI-powered response suggestions</li>
              <li>Seamless CRM integrations</li>
              <li>Multi-agent inbox for team collaboration</li>
            </ul>
            <Link to="/signup" className="btn-primary">
              Start Your Free Trial
            </Link>
          </div>
          <div className="product-demo-visual">
            <div className="browser-mockup">
              <div className="browser-bar">
                <span className="browser-dot red"></span>
                <span className="browser-dot yellow"></span>
                <span className="browser-dot green"></span>
              </div>
              <div className="browser-content">
                <img
                  src={dashboardPreview}
                  alt="MessageOS Dashboard"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="demo-annotation ann-1">⚡ Auto-replies</div>
            <div className="demo-annotation ann-2">🔗 CRM Sync</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==========================================
// PRICING SECTION
// ==========================================
const PricingSection = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const [pricingCalcVisible, setPricingCalcVisible] = useState(false);
  const switchRef = useRef(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px 0px" });

  // ── Dynamic data from public API ──────────────────────────────────────
  const [plans, setPlans] = useState([]);
  const [rates, setRates] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const [plansRes, ratesRes] = await Promise.all([
          axiosConfig.get('/public/plans'),
          axiosConfig.get('/public/rates'),
        ]);
        if (plansRes.data?.entity?.plans) {
          const sorted = [...plansRes.data.entity.plans].sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
          );
          setPlans(sorted);
        }
        if (ratesRes.data?.entity?.rates) {
          setRates(ratesRes.data.entity.rates);
        }
      } catch (err) {
        console.error('Failed to load pricing data:', err);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPricingData();
  }, []);

  // Derive CTA text from plan_name
  const ctaText = (planName) => {
    const map = {
      starter: 'Start Free Trial',
      professional: 'Get Started',
      enterprise: 'Contact Sales',
    };
    return map[(planName || '').toLowerCase()] || 'Get Started';
  };

  const handleToggle = () => {
    const goingAnnual = isMonthly;
    setIsMonthly(!isMonthly);

    // Confetti when switching to annual
    if (goingAnnual && switchRef.current) {
      const rect = switchRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { x: x / window.innerWidth, y: y / window.innerHeight },
        colors: ["#5B8DEF", "#00FFE5", "#B39DFC", "#FF6B35"],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        shapes: ["circle"],
      });
    }
  };

  return (
    <section
      className="pricing-section-new landing-section"
      id="pricing"
      ref={sectionRef}
    >
      <div className="landing-container">
        {/* Header */}
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Choose the plan that works for you.</p>
        </div>

        {/* Pricing info boxes */}
        <div className="pricing-info-boxes">
          <div className="pricing-info-box">
            <span className="pricing-info-icon">🏷️</span>
            <div className="pricing-info-content">
              <h4>Platform Fee</h4>
              <p>
                Monthly subscription for MessageOS features, team access,
                automation, and support.
              </p>
            </div>
          </div>
          <span className="pricing-info-plus">+</span>
          <div className="pricing-info-box">
            <span className="pricing-info-icon">💬</span>
            <div className="pricing-info-content">
              <h4>WhatsApp Conversation Charges</h4>
              {rates ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 6 }}>
                  {Object.values(rates).map((r) => (
                    <span key={r.category} style={{
                      fontSize: '12px',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 6,
                      padding: '2px 8px',
                      textTransform: 'capitalize',
                    }}>
                      {r.category}: <strong>₹{parseFloat(r.rate).toFixed(2)}</strong>/conv
                    </span>
                  ))}
                </div>
              ) : (
                <p>
                  Pay-as-you-go charges by WhatsApp for each conversation.{" "}
                  <a
                    href="https://developers.facebook.com/docs/whatsapp/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pricing-wa-link"
                  >
                    View WhatsApp Pricing →
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Toggle */}
        <div className="pricing-toggle-bar">
          <span className={isMonthly ? "active" : ""}>Monthly</span>
          <button
            ref={switchRef}
            className={`pricing-switch ${!isMonthly ? "annual" : ""}`}
            onClick={handleToggle}
            aria-label="Toggle annual billing"
          >
            <span className="pricing-switch-thumb" />
          </button>
          <span className={!isMonthly ? "active" : ""}>
            Annual{" "}
            <span className="pricing-save-badge">Save 17%</span>
          </span>
        </div>

        {/* Loading skeleton */}
        {plansLoading && (
          <div className="pricing-cards">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="pricing-card"
                style={{
                  minHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  padding: '2rem',
                  opacity: 0.45,
                }}
              >
                {[80, 48, 32, 100, 90, 110].map((w, j) => (
                  <div key={j} style={{
                    height: j === 0 ? 20 : 14,
                    width: `${w}%`,
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 6,
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }} />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Cards */}
        {!plansLoading && (
          <div className="pricing-cards">
            {plans.map((plan, idx) => {
              const isPopular = (plan.plan_name || '').toLowerCase() === 'professional'
                || (plans.length === 3 && idx === 1);
              const price = parseFloat(
                isMonthly ? plan.monthly_price : plan.annual_price
              ) || 0;

              return (
                <motion.div
                  key={plan.planid || idx}
                  className={`pricing-card ${isPopular ? "popular" : ""}`}
                  initial={{ y: 50, opacity: 0 }}
                  animate={
                    isInView
                      ? {
                          y: isPopular ? -12 : 0,
                          opacity: 1,
                          scale: isPopular ? 1.03 : 1,
                        }
                      : {}
                  }
                  transition={{
                    duration: 0.8,
                    type: "spring",
                    stiffness: 100,
                    damping: 25,
                    delay: 0.15 + idx * 0.12,
                  }}
                >
                  {isPopular && (
                    <div className="pricing-popular-badge">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{ marginRight: 4 }}
                      >
                        <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.4 5.7 21l2.3-7L2 9.4h7.6z" />
                      </svg>
                      Popular
                    </div>
                  )}

                  <p className="pricing-plan-name">{plan.display_name}</p>

                  <div className="pricing-amount">
                    <span className="pricing-currency">₹</span>
                    <NumberFlow
                      value={price}
                      transformTiming={{ duration: 500, easing: "ease-out" }}
                      willChange
                      className="pricing-number"
                    />
                    <span className="pricing-period">
                      / {isMonthly ? "mo" : "yr"}
                    </span>
                  </div>

                  <p className="pricing-billed">
                    {isMonthly ? "billed monthly" : "billed annually"}
                  </p>

                  <ul className="pricing-feature-list">
                    {(plan.features || []).map((f, i) => (
                      <li key={i}>
                        <svg
                          className="pricing-check-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pricing-card-divider" />

                  <Link
                    to="/signup"
                    className={`pricing-cta ${isPopular ? "primary" : "outline"}`}
                  >
                    {ctaText(plan.plan_name)}
                  </Link>

                  <p className="pricing-card-desc">{plan.description}</p>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* WhatsApp Pricing Calculator */}
      <div style={{ textAlign: 'center', marginTop: 32 }}>
        <button
          onClick={() => setPricingCalcVisible(true)}
          style={{
            background: 'linear-gradient(135deg, #25D366, #128C7E)',
            color: '#fff',
            border: 'none',
            borderRadius: 9999,
            padding: '12px 32px',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            boxShadow: '0 2px 8px rgba(37,211,102,0.25)',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          💬 WhatsApp Pricing Calculator
        </button>
        <p style={{ fontSize: 13, color: '#65676b', marginTop: 8 }}>
          Check Meta's official per-message rates by country
        </p>
      </div>

      <PricingCalculator visible={pricingCalcVisible} onClose={() => setPricingCalcVisible(false)} />
    </section>
  );
};

// ==========================================
// CTA BANNER SECTION
// ==========================================
const CTABannerSection = () => {
  return (
    <section className="cta-section">
      <div className="cta-glow glow-1"></div>
      <div className="cta-glow glow-2"></div>
      <div className="landing-container">
        <div className="cta-content">
          <h2>Ready to Transform Your Business Communication?</h2>
          <p>
            Join thousands of businesses already using MessageOS to grow their
            revenue through WhatsApp
          </p>
          <Link to="/signup" className="btn-primary">
            Start Your Free Trial Today
          </Link>
        </div>
      </div>
    </section>
  );
};

// ==========================================
// MAIN LANDING PAGE COMPONENT
// ==========================================
const LandingPageNew = () => {
  // To restore light/dark switching later, uncomment this state block.
  // const [theme, setTheme] = useState(() => {
  //   const saved = localStorage.getItem("theme");
  //   return saved || "dark";
  // });
  const theme = "light";
  const location = useLocation();

  // Keep the landing page in light mode only.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    // To persist theme switching later, uncomment the next line.
    // localStorage.setItem("theme", theme);
  }, [theme]);

  // Smooth scroll to hash
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          const navbarHeight = 80;
          const y = el.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
          window.scrollTo({ top: y, behavior: "smooth" });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location.hash]);

  // To restore the switcher later, uncomment this handler.
  // const toggleTheme = () => {
  //   setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  // };

  return (
    <div className="landing-page-new">
      <TopBar theme={theme} />
      <HeroSection />
      <PlatformPerformance />
      <FeaturesSection />
      <HowItWorksSection />
      <ProductDemoSection />
      <WhyUsSection />
      <PricingSection />
      <CTABannerSection />
      <FooterBar theme={theme} />
    </div>
  );
};

export default LandingPageNew;
